import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Shield, Gift, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

function useDeviceDetect() {
  const [device, setDevice] = useState('desktop');
  useEffect(() => {
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod/i.test(ua)) setDevice('ios');
    else if (/Android/i.test(ua)) setDevice('android');
    else setDevice('desktop');
  }, []);
  return device;
}

function GoogleOneTap({ onSuccess, onError }) {
  const { loginWithGoogle, GOOGLE_CLIENT_ID } = useAuth();
  const btnRef = useRef(null);
  const initialized = useRef(false);

  const handleCredential = useCallback(async (response) => {
    try {
      await loginWithGoogle(response.credential);
      onSuccess?.();
    } catch (err) {
      onError?.(err.response?.data?.detail || 'Erreur Google');
    }
  }, [loginWithGoogle, onSuccess, onError]);

  useEffect(() => {
    if (initialized.current) return;
    const init = () => {
      if (!window.google?.accounts?.id) return;
      initialized.current = true;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredential,
        auto_select: false,
      });
      if (btnRef.current) {
        window.google.accounts.id.renderButton(btnRef.current, {
          type: 'standard', theme: 'filled_black', size: 'large',
          text: 'continue_with', shape: 'pill', logo_alignment: 'left',
          width: Math.min(btnRef.current.offsetWidth || 320, 400),
        });
      }
    };
    if (window.google?.accounts?.id) { init(); }
    else {
      const s = document.createElement('script');
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true; s.onload = init;
      document.head.appendChild(s);
    }
  }, [GOOGLE_CLIENT_ID, handleCredential]);

  return <div ref={btnRef} data-testid="landing-google-btn" className="[&>div]:!w-full" />;
}

const APPLE_STORE_URL = 'https://apps.apple.com/app/ahoow/id6742487937';
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.ahoow.app';

export default function Landing() {
  const device = useDeviceDetect();
  const navigate = useNavigate();
  const [authError, setAuthError] = useState('');

  return (
    <div className="min-h-screen bg-brand-dark overflow-x-hidden" data-testid="landing-page">
      <section className="relative h-screen flex flex-col">
        <div className="absolute inset-0">
          <img src="romantic-bg.png" alt="Couple romantique" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0015]/90 via-[#0a0015]/60 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[#0a0015] to-transparent" />
        </div>

        <header className="relative z-10 flex items-center justify-between px-8 lg:px-16 py-5">
          <div className="flex items-center gap-3">
            <img src="logo.png" alt="AHOOW" className="h-10 w-10 rounded-xl" />
            <span className="text-2xl font-black text-white tracking-wide" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              AH<span className="text-brand-pink">OO</span>W
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-sm font-semibold text-white/80 hover:text-white transition-colors px-5 py-2.5 rounded-full border border-white/20 hover:border-white/40 backdrop-blur-sm" data-testid="landing-login-btn">
              Connexion
            </Link>
            <Link to="/register" className="text-sm font-bold text-white px-6 py-2.5 rounded-full bg-gradient-to-r from-brand-pink to-rose-500 hover:shadow-lg hover:shadow-brand-pink/30 transition-all hover:-translate-y-0.5" data-testid="landing-register-btn">
              S'inscrire
            </Link>
          </div>
        </header>

        <div className="relative z-10 flex-1 flex items-center px-8 lg:px-16 pb-32">
          <div className="max-w-xl">
            <h1 className="text-5xl lg:text-7xl font-black text-white leading-[1.1] mb-6">
              Trouver<br />l'amour.
            </h1>
            <p className="text-xl lg:text-2xl text-white/80 font-light italic leading-relaxed mb-3" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
              Decouvrez votre plus belle histoire avec Ahoow.
            </p>
            <p className="text-lg text-white/60 font-light italic mb-8" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
              et gagnez des recompenses.
            </p>

            {authError && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-xl px-4 py-3 mb-4">{authError}</div>
            )}

            <div className="flex flex-col gap-4 max-w-sm">
              <GoogleOneTap onSuccess={() => navigate('/discover')} onError={(msg) => setAuthError(msg)} />

              {device === 'ios' && (
                <a href={APPLE_STORE_URL} target="_blank" rel="noopener noreferrer"
                  className="group flex items-center justify-center gap-3 text-white font-semibold text-sm px-8 py-3.5 rounded-full bg-black border border-white/20 hover:border-white/40 transition-all hover:-translate-y-0.5"
                  data-testid="cta-appstore"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white"><path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 16.56 2.93 11.3 4.7 7.72C5.57 5.94 7.36 4.86 9.28 4.84C10.56 4.81 11.78 5.72 12.57 5.72C13.36 5.72 14.85 4.62 16.4 4.8C17.07 4.83 18.91 5.07 20.07 6.78C19.97 6.84 17.62 8.15 17.64 11C17.67 14.38 20.61 15.46 20.64 15.47C20.61 15.54 20.18 17.04 19.12 18.57L18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/></svg>
                  Telecharger sur l'App Store
                </a>
              )}

              {device === 'android' && (
                <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer"
                  className="group flex items-center justify-center gap-3 text-white font-semibold text-sm px-8 py-3.5 rounded-full bg-black border border-white/20 hover:border-white/40 transition-all hover:-translate-y-0.5"
                  data-testid="cta-playstore"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white"><path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/></svg>
                  Telecharger sur Google Play
                </a>
              )}

              {device === 'desktop' && (
                <div className="flex gap-3">
                  <Link to="/register"
                    className="flex-1 group inline-flex items-center justify-center gap-2 text-white font-bold text-sm px-6 py-3.5 rounded-full bg-gradient-to-r from-brand-pink via-rose-500 to-brand-violet hover:shadow-xl hover:shadow-brand-pink/30 transition-all hover:-translate-y-0.5"
                    data-testid="hero-cta"
                  >
                    S'inscrire <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link to="/login"
                    className="flex-1 inline-flex items-center justify-center text-white/70 font-semibold text-sm px-6 py-3.5 rounded-full border border-white/15 hover:border-white/30 hover:text-white backdrop-blur-sm transition-all"
                  >
                    Connexion
                  </Link>
                </div>
              )}

              {device !== 'desktop' && (
                <Link to="/register" className="text-center text-sm text-white/50 hover:text-white/80 transition-colors" data-testid="hero-cta-mobile">
                  ou s'inscrire avec un email <ArrowRight size={14} className="inline ml-1" />
                </Link>
              )}
            </div>

            <p className="text-white/40 text-sm mt-6">
              Rejoignez-nous sur <span className="font-bold text-white/60">WWW.AHOOW.COM</span>
            </p>
          </div>
        </div>
      </section>

      <section className="relative px-8 lg:px-16 py-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
            Pourquoi choisir <span className="gradient-text">Ahoow</span> ?
          </h2>
          <p className="text-white/40 text-lg max-w-xl mx-auto">
            Offres exclusives et recompenses a la cle.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {[
            { icon: Heart, title: 'Matchs intelligents', desc: 'Algorithme Love Score pour des compatibilites precises et authentiques', color: 'from-brand-pink to-rose-500' },
            { icon: MessageCircle, title: 'Chat immersif', desc: 'Messages vocaux, reactions emoji, accusees de lecture en temps reel', color: 'from-brand-blue to-cyan-500' },
            { icon: Shield, title: 'Profils verifies', desc: 'Verification photo pour des rencontres en toute confiance et securite', color: 'from-emerald-500 to-green-500' },
            { icon: Gift, title: 'Parrainage & recompenses', desc: 'Invitez vos amis, gagnez des credits, des badges et des avantages', color: 'from-brand-violet to-purple-500' },
          ].map(({ icon: Icon, title, desc, color }) => (
            <div key={title} className="glass-card-hover p-7 group">
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                <Icon size={24} className="text-white" />
              </div>
              <h3 className="text-base font-bold text-white mb-3">{title}</h3>
              <p className="text-sm text-white/40 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="relative px-8 lg:px-16 py-24">
        <div className="max-w-4xl mx-auto glass-card p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-pink/10 via-brand-violet/10 to-brand-blue/10" />
          <div className="relative z-10">
            <img src="logo.png" alt="AHOOW" className="h-16 w-16 rounded-2xl mx-auto mb-6" />
            <h2 className="text-3xl font-black text-white mb-4">
              {device === 'ios' ? 'Disponible sur iPhone' : device === 'android' ? 'Disponible sur Android' : 'Commencez votre aventure'}
            </h2>
            <p className="text-white/50 text-lg mb-8 max-w-lg mx-auto">
              {device !== 'desktop'
                ? "Telechargez l'application gratuite et trouvez l'amour pres de chez vous."
                : 'Inscrivez-vous gratuitement et decouvrez des milliers de profils pres de chez vous.'}
            </p>

            {device === 'desktop' && (
              <div className="flex justify-center gap-4">
                <Link to="/register"
                  className="inline-flex items-center gap-3 text-white font-bold text-base px-10 py-4 rounded-full bg-gradient-to-r from-brand-pink to-brand-violet hover:shadow-xl hover:shadow-brand-pink/30 transition-all hover:-translate-y-1"
                >
                  Commencer maintenant <ArrowRight size={18} />
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      <footer className="px-8 lg:px-16 py-8 border-t border-white/[0.06]">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <img src="logo.png" alt="AHOOW" className="h-8 w-8 rounded-lg" />
            <span className="text-sm font-bold text-white/40">AH<span className="text-brand-pink/60">OO</span>W &copy; 2026</span>
          </div>
          <div className="flex gap-6 text-sm text-white/30">
            <Link to="/privacy" className="hover:text-white/60 transition-colors" data-testid="footer-privacy">Confidentialite</Link>
            <Link to="/terms" className="hover:text-white/60 transition-colors" data-testid="footer-terms">Conditions</Link>
            <Link to="/data-deletion" className="hover:text-white/60 transition-colors" data-testid="footer-deletion">Suppression des donnees</Link>
            <a href="mailto:support@ahoow.com" className="hover:text-white/60 transition-colors" data-testid="footer-contact">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
